import React, { useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import * as opexService from "../../../services/opexService";
import { getVendorCurrency } from "../../../utils/currency";

const emptyForm = {
  title: "",
  category_id: "",
  type: "ONE_TIME",
  amount: "",
  frequency: "MONTHLY",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  notes: "",
};

// Add/edit an Operating Expense definition (one-time or recurring). Saving a
// RECURRING item seeds next_due_date = start_date server-side; each payment
// against it later auto-advances that date by its frequency.
function ItemFormPanel({ show, onHide, item, categories, onManageCategories, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const currency = getVendorCurrency();

  useEffect(() => {
    if (item) {
      setForm({
        title: item.title,
        category_id: item.category_id,
        type: item.type,
        amount: item.amount,
        frequency: item.frequency || "MONTHLY",
        start_date: item.start_date?.slice(0, 10) || emptyForm.start_date,
        end_date: item.end_date?.slice(0, 10) || "",
        notes: item.notes || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [item, show]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.category_id) return toast.error("Select a category");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter an amount greater than 0");

    const payload = {
      title: form.title.trim(),
      category_id: Number(form.category_id),
      type: form.type,
      amount: Number(form.amount),
      frequency: form.type === "RECURRING" ? form.frequency : undefined,
      start_date: form.start_date,
      end_date: form.end_date || undefined,
      notes: form.notes || undefined,
    };

    setSubmitting(true);
    try {
      const res = item ? await opexService.updateItem(item.id, payload) : await opexService.createItem(payload);
      if (res?.success === false) {
        toast.error(res.message || "Could not save expense");
      } else {
        toast.success(item ? "Expense updated" : "Expense created");
        onSaved();
      }
    } catch (err) {
      toast.error(err.message || "Could not save expense");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SlidePanel show={show} onHide={onHide} title={item ? "Edit Operating Expense" : "New Operating Expense"} width={460}>
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Title</Form.Label>
          <Form.Control placeholder="e.g. Office Rent, DEWA Bill" value={form.title} onChange={set("title")} required />
        </Form.Group>

        <Form.Group className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <Form.Label className="mb-0">Category</Form.Label>
            <button
              type="button"
              onClick={onManageCategories}
              style={{ border: "none", background: "none", color: "var(--button-color)", fontSize: 13, fontWeight: 600 }}
            >
              Manage
            </button>
          </div>
          <Form.Select value={form.category_id} onChange={set("category_id")} required>
            <option value="">Select category</option>
            {categories
              .filter((c) => c.status === "ACTIVE" || c.id === Number(form.category_id))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              label="One-time"
              name="opex-type"
              checked={form.type === "ONE_TIME"}
              onChange={() => setForm((f) => ({ ...f, type: "ONE_TIME" }))}
            />
            <Form.Check
              type="radio"
              label="Recurring"
              name="opex-type"
              checked={form.type === "RECURRING"}
              onChange={() => setForm((f) => ({ ...f, type: "RECURRING" }))}
            />
          </div>
        </Form.Group>

        {form.type === "RECURRING" && (
          <Form.Group className="mb-3">
            <Form.Label>Frequency</Form.Label>
            <Form.Select value={form.frequency} onChange={set("frequency")}>
              <option value="MONTHLY">Monthly</option>
              <option value="QUARTERLY">Quarterly</option>
              <option value="YEARLY">Yearly</option>
            </Form.Select>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Amount ({currency})</Form.Label>
          <Form.Control type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")} required />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{form.type === "RECURRING" ? "First due date" : "Due date"}</Form.Label>
          <Form.Control type="date" value={form.start_date} onChange={set("start_date")} required />
        </Form.Group>

        {form.type === "RECURRING" && (
          <Form.Group className="mb-3">
            <Form.Label>End date (optional)</Form.Label>
            <Form.Control type="date" value={form.end_date} onChange={set("end_date")} />
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Notes (optional)</Form.Label>
          <Form.Control as="textarea" rows={2} value={form.notes} onChange={set("notes")} />
        </Form.Group>

        <div className="d-flex justify-content-end gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e5e7eb" }}>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} style={{ background: "var(--button-color)", border: "none", color: "#fff" }}>
            {submitting ? "Saving…" : "Save"}
          </Button>
        </div>
      </Form>
    </SlidePanel>
  );
}

export default ItemFormPanel;
